import { getAddress } from '@ethersproject/address'
import { ChainId, Currency, FORTTOKEN, NATIVE, SICLE, STLOSTOKEN, Token } from '@swapsicledex/swapsicle-sdk'
import { Feature } from 'app/enums'
import { Chef, PairType } from 'app/features/onsen/enum'
import { usePositions } from 'app/features/onsen/hooks'
import { featureEnabled } from 'app/functions'
import { aprToApy } from 'app/functions/convert'
import {
  useAverageBlockTime,
  useEthPrice,
  useFantomPrice,
  useSTLOSPrice,
  useFORTPrice,
  useFarms,
  useKashiPairs,
  useMasterChefV1SushiPerBlock,
  useMasterChefV1TotalAllocPoint,
  useMaticPrice,
  useOneDayBlock,
  useSushiPairs,
  useSushiPrice,
} from 'app/services/graph'
import { useActiveWeb3React } from 'app/services/web3'
import { useCallback, useMemo } from 'react'

import { useAllTokens } from './Tokens'
import { FARM_POOLS_V1 } from 'app/constants/farm-pools-v1'

export default function useFarmRewards() {
  const { chainId } = useActiveWeb3React()
  const positions = usePositions(chainId)
  const block1d = useOneDayBlock({ chainId, shouldFetch: !!chainId })
  const farms = FARM_POOLS_V1[chainId]
  const farmAddresses = useMemo(() => farms.map((farm) => farm.pair.id.toLowerCase()), [farms])
  const allTokens = useAllTokens()
  const swapPairs = useSushiPairs({
    chainId,
    variables: {
      where: {
        id_in: farmAddresses,
      },
    },
    shouldFetch: !!farmAddresses,
  })
  const swapPairs1d = useSushiPairs({
    chainId,
    variables: {
      block: block1d,
      where: {
        id_in: farmAddresses,
      },
    },
    shouldFetch: !!block1d && !!farmAddresses,
  })

  const kashiPairs = useKashiPairs({
    chainId,
    variables: { where: { id_in: farmAddresses } },
    shouldFetch: !!farmAddresses && featureEnabled(Feature.KASHI, chainId),
  })

  const masterChefV1TotalAllocPoint = useMasterChefV1TotalAllocPoint()
  const masterChefV1SushiPerBlock = useMasterChefV1SushiPerBlock()

  const sushiPrice = useSushiPrice()
  const sTLOSPrice = useSTLOSPrice()
  const FORTPrice = useFORTPrice()
  const ethPrice = useEthPrice()
  const maticPrice = useMaticPrice()
  const fantomPrice = useFantomPrice()

  // Temp fix due to useAverageBlockTime not working
  //const averageBlockTime = useAverageBlockTime({ chainId })
  const averageBlockTime =
    chainId == ChainId.AVALANCHE ? 2 : chainId == ChainId.TELOS ? 0.5 : chainId == ChainId.TELOS_TESTNET ? 0.5 : 1

  const blocksPerDay = 86400 / Number(averageBlockTime)

  const MASTERCHEF_V2_POPSPERBLOCK = {
    [ChainId.TELOS_TESTNET]: 130000000000000000,
    [ChainId.TELOS]: 130000000000000000,
    [ChainId.AVALANCHE]: 0, // 105000000000000000 set to 0 02/08/23
  }

  // @ts-ignore TYPE NEEDS FIXING
  const map = useCallback(
    // @ts-ignore TYPE NEEDS FIXING
    (pool) => {
      // TODO: Deal with inconsistencies between properties on subgraph
      pool.owner = pool?.owner || pool?.masterChef || pool?.miniChef
      pool.balance = pool?.balance || pool?.slpBalance

      // @ts-ignore TYPE NEEDS FIXING
      const swapPair = swapPairs?.find((pair) => pair.id === pool.pair)
      // @ts-ignore TYPE NEEDS FIXING
      const swapPair1d = swapPairs1d?.find((pair) => pair.id === pool.pair)
      // @ts-ignore TYPE NEEDS FIXING
      const kashiPair = kashiPairs?.find((pair) => pair.id === pool.pair)

      const pair = swapPair || kashiPair

      const type = swapPair ? PairType.SWAP : PairType.KASHI

      const blocksPerHour = 3600 / averageBlockTime

      function getRewards() {
        // TODO: Some subgraphs give sushiPerBlock & sushiPerSecond, and mcv2 gives nothing
        const sushiPerBlock =
          pool?.owner?.sushiPerBlock / 1e18 ||
          (pool?.owner?.sushiPerSecond / 1e18) * averageBlockTime ||
          masterChefV1SushiPerBlock

        // @ts-ignore TYPE NEEDS FIXING
        const rewardPerBlock = (pool.allocPoint / pool.owner.totalAllocPoint) * sushiPerBlock

        const defaultReward = {
          currency: SICLE[chainId],
          rewardPerBlock,
          rewardPerDay: rewardPerBlock * blocksPerDay,
          rewardPrice: sushiPrice,
        }

        const telosDuelReward = {
          currency: STLOSTOKEN[chainId],
          rewardPerBlock,
          rewardPerDay: rewardPerBlock * blocksPerDay,
          rewardPrice: sTLOSPrice,
        }

        const FORTReward = {
          currency: FORTTOKEN[chainId],
          rewardPerBlock,
          rewardPerDay: rewardPerBlock * blocksPerDay,
          rewardPrice: FORTPrice,
        }

        let rewards: { currency: Currency; rewardPerBlock: number; rewardPerDay: number; rewardPrice: number }[] = [
          // @ts-ignore TYPE NEEDS FIXING
          defaultReward,
        ]

        if (pool.chef === Chef.MASTERCHEF_V2) {
          // Stupidly this has been HARDCODED FOR TELOS -.- .... needs fixing before deployment on another chain
          const sushiPerBlock =
            ((pool.allocPoint / pool.masterChef.totalRegularAllocPoint) *
              (MASTERCHEF_V2_POPSPERBLOCK[chainId] ? MASTERCHEF_V2_POPSPERBLOCK[chainId] : 0)) /
            1e18

          const sushiPerSecond = sushiPerBlock * averageBlockTime //0.5
          const sushiPerDay = sushiPerBlock * blocksPerDay //172800

          const reward = {}

          // @ts-ignore TYPE NEEDS FIXING
          rewards[0] = {
            ...defaultReward,
            rewardPerBlock: sushiPerBlock,
            rewardPerDay: sushiPerDay,
          }

          // @ts-ignore TYPE NEEDS FIXING
          if (chainId in reward) {
            // @ts-ignore TYPE NEEDS FIXING
            rewards[1] = reward[chainId]
          }
        } else if (pool.chef === Chef.MASTERCHEF_V2_DUEL_REWARD) {
          // Stupidly this has been HARDCODED FOR TELOS -.- .... needs fixing before deployment on another chain

          const totalExtraPerBlock = 1167750000000000 // copied from contract - should pull automatically
          const totalPOPSPerBlock = 25000000000000000 // copied from contract - should pull automatically

          const sushiPerSecond =
            ((pool.allocPoint / pool.masterChef.totalRegularAllocPoint) * (totalPOPSPerBlock * 2)) / 1e18
          const extraPerSecond =
            ((pool.allocPoint / pool.masterChef.totalRegularAllocPoint) * (totalExtraPerBlock * 2)) / 1e18

          const sushiPerBlock = sushiPerSecond * averageBlockTime // 0.5
          const sushiPerDay = sushiPerBlock * blocksPerDay //172800

          const extraPerBlock = extraPerSecond * averageBlockTime //0.5
          const extraPerDay = extraPerBlock * blocksPerDay //172800

          const reward = {}

          // @ts-ignore TYPE NEEDS FIXING
          rewards[0] = {
            ...telosDuelReward,
            rewardPerBlock: extraPerBlock,
            rewardPerDay: extraPerDay,
          }

          // @ts-ignore TYPE NEEDS FIXING
          rewards[1] = {
            ...defaultReward,
            rewardPerBlock: sushiPerBlock,
            rewardPerDay: sushiPerDay,
          }

          // @ts-ignore TYPE NEEDS FIXING
          if (chainId in reward) {
            // @ts-ignore TYPE NEEDS FIXING
            rewards[1] = reward[chainId]
          }
        } else if (pool.chef === Chef.MASTERCHEF_V2_DUEL_REWARD_FORTIS) {
          const totalExtraPerBlock = 790000000000000 // copied from contract - should pull automatically
          const totalPOPSPerBlock = 45448000000000000 // copied from contract - should pull automatically

          const sushiPerSecond =
            ((pool.allocPoint / pool.masterChef.totalRegularAllocPoint) * (totalPOPSPerBlock * 2)) / 1e18
          const extraPerSecond =
            ((pool.allocPoint / pool.masterChef.totalRegularAllocPoint) * (totalExtraPerBlock * 2)) / 1e18

          const sushiPerBlock = sushiPerSecond * averageBlockTime // 0.5
          const sushiPerDay = sushiPerBlock * blocksPerDay //172800

          const extraPerBlock = extraPerSecond * averageBlockTime //0.5
          const extraPerDay = extraPerBlock * blocksPerDay //172800

          const reward = {}

          // @ts-ignore TYPE NEEDS FIXING
          rewards[0] = {
            ...telosDuelReward,
            rewardPerBlock: extraPerBlock,
            rewardPerDay: extraPerDay,
          }

          // @ts-ignore TYPE NEEDS FIXING
          rewards[1] = {
            ...FORTReward,
            rewardPerBlock: sushiPerBlock,
            rewardPerDay: sushiPerDay,
          }

          // @ts-ignore TYPE NEEDS FIXING
          if (chainId in reward) {
            // @ts-ignore TYPE NEEDS FIXING
            rewards[1] = reward[chainId]
          }
        }
        // else if (pool.chef === Chef.MINICHEF && chainId !== ChainId.MATIC && chainId !== ChainId.ARBITRUM) {
        //   const sushiPerSecond =
        //     ((pool.allocPoint / pool.miniChef.totalAllocPoint) * pool.miniChef.sushiPerSecond) / 1e18
        //   const sushiPerBlock = sushiPerSecond * averageBlockTime
        //   const sushiPerDay = sushiPerBlock * blocksPerDay

        //   const rewardPerSecond =
        //     ((pool.allocPoint / pool.miniChef.totalAllocPoint) * pool.rewarder.rewardPerSecond) / 1e18

        //   const rewardPerBlock = rewardPerSecond * averageBlockTime

        //   const rewardPerDay = rewardPerBlock * blocksPerDay

        //   const reward = {
        //     [ChainId.MATIC]: {
        //       currency: NATIVE[ChainId.MATIC],
        //       rewardPerBlock,
        //       rewardPerDay: rewardPerSecond * 86400,
        //       rewardPrice: maticPrice,
        //     },
        //     // [ChainId.XDAI]: {
        //     //   currency: XDAI_TOKENS.GNO,
        //     //   rewardPerBlock,
        //     //   rewardPerDay: rewardPerSecond * 86400,
        //     //   rewardPrice: gnoPrice,
        //     // },
        //     [ChainId.HARMONY]: {
        //       currency: NATIVE[ChainId.HARMONY],
        //       rewardPerBlock,
        //       rewardPerDay: rewardPerSecond * 86400,
        //       rewardPrice: onePrice,
        //     },
        //     [ChainId.CELO]: {
        //       currency: NATIVE[ChainId.CELO],
        //       rewardPerBlock,
        //       rewardPerDay: rewardPerSecond * 86400,
        //       rewardPrice: celoPrice,
        //     },
        //     [ChainId.MOONRIVER]: {
        //       currency: NATIVE[ChainId.MOONRIVER],
        //       rewardPerBlock,
        //       rewardPerDay: rewardPerSecond * 86400,
        //       rewardPrice: movrPrice,
        //     },
        //     [ChainId.FUSE]: {
        //       currency: NATIVE[ChainId.FUSE],
        //       rewardPerBlock,
        //       rewardPerDay: rewardPerSecond * 86400,
        //       rewardPrice: fusePrice,
        //     },
        //     [ChainId.FANTOM]: {
        //       currency: NATIVE[ChainId.FANTOM],
        //       rewardPerBlock,
        //       rewardPerDay: rewardPerSecond * 86400,
        //       rewardPrice: fantomPrice,
        //     },
        //     [ChainId.MOONBEAM]: {
        //       currency: NATIVE[ChainId.MOONBEAM],
        //       rewardPerBlock,
        //       rewardPerDay: rewardPerSecond * 86400,
        //       rewardPrice: glimmerPrice,
        //     },
        //   }

        //   if (chainId === ChainId.FUSE) {
        //     // Secondary reward only
        //     rewards[0] = reward[ChainId.FUSE]
        //   } else {
        //     // @ts-ignore TYPE NEEDS FIXING
        //     rewards[0] = {
        //       ...defaultReward,
        //       rewardPerBlock: sushiPerBlock,
        //       rewardPerDay: sushiPerDay,
        //     }
        //     // @ts-ignore TYPE NEEDS FIXING
        //     if (chainId in reward) {
        //       // @ts-ignore TYPE NEEDS FIXING
        //       rewards[1] = reward[chainId]
        //     }
        //   }
        // } else if (chainId === ChainId.MATIC || chainId === ChainId.ARBITRUM) {
        //   if (pool.rewarder.rewardPerSecond !== '0') {
        //     const rewardPerSecond =
        //       pool.rewarder.totalAllocPoint === '0'
        //         ? pool.rewarder.rewardPerSecond / 10 ** pool.rewardToken.decimals
        //         : ((pool.allocPoint / pool.rewarder.totalAllocPoint) * pool.rewarder.rewardPerSecond) / 1e18
        //     const rewardPerBlock = rewardPerSecond * averageBlockTime
        //     const rewardPerDay = rewardPerBlock * blocksPerDay
        //     const rewardPrice = pool.rewardToken.derivedETH * ethPrice

        //     const address = getAddress(pool.rewardToken.id)

        //     rewards[1] = {
        //       currency:
        //         address in allTokens
        //           ? allTokens[address]
        //           : new Token(
        //               ChainId.ETHEREUM,
        //               getAddress(pool.rewardToken.id),
        //               Number(pool.rewardToken.decimals),
        //               pool.rewardToken.symbol,
        //               pool.rewardToken.name
        //             ),
        //       rewardPerBlock,
        //       rewardPerDay,
        //       rewardPrice,
        //     }
        //   }
        // } else if (pool.chef === Chef.OLD_FARMS) {
        //   const sushiPerSecond =
        //     ((pool.allocPoint / pool.miniChef.totalAllocPoint) * pool.miniChef.sushiPerSecond) / 1e18
        //   const sushiPerBlock = sushiPerSecond * averageBlockTime
        //   const sushiPerDay = sushiPerBlock * blocksPerDay

        //   const rewardPerSecond =
        //     ((pool.allocPoint / pool.miniChef.totalAllocPoint) * pool.rewarder.rewardPerSecond) / 1e18

        //   const rewardPerBlock = rewardPerSecond * averageBlockTime

        //   const rewardPerDay = rewardPerBlock * blocksPerDay

        //   const reward = {
        //     [ChainId.CELO]: {
        //       currency: NATIVE[ChainId.CELO],
        //       rewardPerBlock,
        //       rewardPerDay: rewardPerSecond * 86400,
        //       rewardPrice: celoPrice,
        //     },
        //   }

        //   // @ts-ignore TYPE NEEDS FIXING
        //   rewards[0] = {
        //     ...defaultReward,
        //     rewardPerBlock: sushiPerBlock,
        //     rewardPerDay: sushiPerDay,
        //   }

        //   // @ts-ignore TYPE NEEDS FIXING
        //   if (chainId in reward) {
        //     // @ts-ignore TYPE NEEDS FIXING
        //     rewards[1] = reward[chainId]
        //   }
        // }
        return rewards
      }

      const rewards = getRewards()
      const balance = swapPair ? Number(pool.balance / 1e18) : pool.balance / 10 ** kashiPair.token0.decimals

      const tvl = swapPair
        ? (balance / Number(swapPair.totalSupply)) * Number(swapPair.reserveUSD)
        : balance * kashiPair.token0.derivedETH * ethPrice

      const feeApyPerYear =
        swapPair && swapPair1d
          ? aprToApy((((pair?.volumeUSD - swapPair1d?.volumeUSD) * 0.0025 * 365) / pair?.reserveUSD) * 100, 3650) / 100
          : 0

      const feeApyPerMonth = feeApyPerYear / 12
      const feeApyPerQuarter = feeApyPerYear / 4
      const feeApyPerDay = feeApyPerMonth / 30
      const feeApyPerHour = feeApyPerDay / blocksPerHour

      const roiPerBlock =
        rewards.reduce((previousValue, currentValue) => {
          return previousValue + currentValue.rewardPerBlock * currentValue.rewardPrice
        }, 0) / tvl

      const rewardAprPerHour = roiPerBlock * blocksPerHour
      const rewardAprPerDay = rewardAprPerHour * 24
      const rewardAprPerMonth = rewardAprPerDay * 30
      const rewardAprPerQuarter = rewardAprPerMonth * 3
      const rewardAprPerYear = rewardAprPerMonth * 12

      const roiPerHour = rewardAprPerHour + feeApyPerHour
      const roiPerMonth = rewardAprPerMonth + feeApyPerMonth
      const roiPerQuarter = rewardAprPerQuarter + feeApyPerQuarter
      const roiPerDay = rewardAprPerDay + feeApyPerDay
      const roiPerYear = rewardAprPerYear + feeApyPerYear

      const roiPerYearCompoundDaily = aprToApy(roiPerYear * 100, 365)
      const roiPerYearCompoundWeekly = aprToApy(roiPerYear * 100, 52)
      const roiPerYearCompoundMonthly = aprToApy(roiPerYear * 100, 12)
      const roiPerYearCompoundQuarterly = aprToApy(roiPerYear * 100, 4)
      const roiPerYearCompoundYearly = aprToApy(roiPerYear * 100, 1)

      const position = positions.find((position) => position.id === pool.id && position.chef === pool.chef)

      return {
        ...pool,
        ...position,
        pair: {
          ...pair,
          decimals: pair.type === PairType.KASHI ? Number(pair.asset.tokenInfo.decimals) : 18,
          type,
        },
        balance,
        feeApyPerHour,
        feeApyPerDay,
        feeApyPerMonth,
        feeApyPerYear,
        rewardAprPerHour,
        rewardAprPerDay,
        rewardAprPerMonth,
        rewardAprPerYear,
        roiPerBlock,
        roiPerHour,
        roiPerDay,
        roiPerMonth,
        roiPerQuarter,
        roiPerYear,
        roiPerYearCompoundDaily,
        roiPerYearCompoundWeekly,
        roiPerYearCompoundMonthly,
        roiPerYearCompoundQuarterly,
        roiPerYearCompoundYearly,
        rewards,
        tvl,
      }
    },
    [
      averageBlockTime,
      blocksPerDay,
      chainId,
      ethPrice,
      sTLOSPrice,
      kashiPairs,
      masterChefV1SushiPerBlock,
      positions,
      sushiPrice,
      swapPairs,
      swapPairs1d,
    ]
  )

  const filter = useCallback(
    (farm) => {
      // console.log('useFarmRewards swapPairs : ', swapPairs)
      // console.log('useFarmRewards kashiPairs : ', kashiPairs)
      return (
        // @ts-ignore TYPE NEEDS FIXING
        swapPairs && swapPairs.find((pair) => pair.id === farm.pair)
        // @ts-ignore TYPE NEEDS FIXING
        // (kashiPairs && kashiPairs.find((pair) => pair.id === farm.pair))
      )
    },
    [swapPairs]
  )

  // return useMemo(() => farms, [farms, filter, map])
  return useMemo(() => farms.filter(filter).map(map), [farms, filter, map])
}
